/* eslint-disable @next/next/no-img-element */
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from '../../redux/store'
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper";
import { getOurFleetsListNew } from '../../redux/slices/common';
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Heading from "../heading";
import { useRouter } from "next/navigation";


// const uniqueTypes = Array.from(
//     data.reduce((m, { type }) => {
//         const key = type.trim().toLowerCase();
//         if (!m.has(key)) m.set(key, type.trim());
//         return m;
//     }, new Map()).values()
// );


function FleetCardSkeleton() {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid #eceff2",
                boxShadow: "0 10px 28px rgba(0,0,0,.12)",
                bgcolor: "#fff",
            }}
        >
            {/* image block */}
            <Box sx={{ position: "relative", height: { xs: 180, sm: 210, md: 270 }, p: 1.25 }}>
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ borderRadius: 3 }}
                />
            </Box>

            {/* title */}
            <Box sx={{ px: { xs: 2, md: 3 }, pt: 1, pb: 1.25 }}>
                <Skeleton variant="text" width="70%" height={28} />
            </Box>

            {/* specs bar */}
            <Paper
                elevation={0}
                sx={{
                    mx: { xs: 1.5, md: 2 },
                    mb: { xs: 1.5, md: 2 },
                    px: { xs: 1, md: 1 },
                    py: { xs: 0.5, md: 0.5 },
                    borderRadius: 3,
                    bgcolor: "#f6f6f6",
                    display: "flex",
                    justifyContent: { xs: "space-around", md: "space-between" },
                    alignItems: "center",
                    gap: { xs: 1, md: 0 },
                }}
            >
                {Array.from({ length: 4 }).map((_, i) => (
                    <Stack key={i} alignItems="center" spacing={0.75} sx={{ minWidth: { xs: 52, md: 56 } }}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="text" width={32} height={16} />
                    </Stack>
                ))}
            </Paper>
        </Paper>
    );
}

export default function OurFleetsExact() {
    const dispatch = useDispatch();
    const { ourFleetsRes } = useSelector(
        state => state.common
    );
    const [serviceList, setServiceList] = useState([]);
    const [loader, setLoader] = useState(true);
    const uniqueTypes = Array.from(
        new Set([
            "Popular Car",
            ...(serviceList ?? []).map(i => i.type).filter(Boolean)
        ])
    );

    const [tab, setTab] = useState("Popular Car");

    useEffect(() => {
        setLoader(true);
        dispatch(getOurFleetsListNew());
    }, [dispatch])

    // handle response
    useEffect(() => {
        if (!ourFleetsRes) return;      // wait until we have a response
        setLoader(false);                        // stop loading no matter success/fail

        if (ourFleetsRes.status === 1) {
            setServiceList(ourFleetsRes?.data ?? []);
        } else {
            setServiceList([]);
        }
    }, [ourFleetsRes]);

    return (
        <Box sx={{ py: { xs: 5, md: 4 }, bgcolor: "#f7f9f9" }}>
            {/* Heading */}
            <Stack alignItems="center" spacing={1.2} sx={{ mb: { xs: 2.5, md: 3 } }}>
                <Heading title="Our Fleets" subtitle="Make journey comfortable with" />

                {/* Filter chips — scrollable on mobile */}
                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{
                        mt: { xs: 2, md: 3 },
                        px: { xs: 2, md: 0 },
                        pt: 6,
                        overflowX: { xs: "auto", md: "visible" },
                        whiteSpace: { xs: "nowrap", md: "normal" },
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                    }}
                >
                    {uniqueTypes?.map((t) => {
                        const isActive = tab === t;
                        return (
                            <Chip
                                key={t}
                                label={t}
                                onClick={() => setTab(t)}
                                clickable
                                sx={{
                                    flexShrink: 0,
                                    fontWeight: 700,
                                    borderRadius: "999px",
                                    px: { xs: 1.5, md: 2.5 },
                                    py: { xs: 1, md: 3 },
                                    fontSize: { xs: 14, md: 16 },
                                    background: isActive
                                        ? "linear-gradient(180deg, #75c4c5 0%, #0d8196 100%)"
                                        : "#fff",
                                    color: isActive ? "#fff" : "#000",
                                    "&:hover": {
                                        background: isActive
                                            ? "linear-gradient(180deg, #4bb6b4 0%, #249f9c 100%)"
                                            : "#f7f9f9",
                                    },
                                    "& .MuiChip-label": { px: 0, fontWeight: 700 },
                                }}
                            />
                        );
                    })}
                </Stack>
            </Stack>

            {/* Carousel */}
            <Box
                sx={{
                    position: "relative",
                    maxWidth: 1200,
                    mx: "auto",
                    px: { xs: 0, sm: 2, md: 0 },
                    "& .swiper": { overflow: "hidden" },
                }}
            >
                {/* compute filtered list */}
                {(() => {
                    const filteredCars = serviceList?.filter(
                        (car) => (tab === "Popular Car" || !tab) ? true : car.type === tab
                    ) ?? [];

                    return (
                        <>
                            {/* Prev/Next (show only if ≥3 cars & hide on mobile, show ≥md) */}
                            {filteredCars.length >= 3 && (
                                <>
                                    <IconButton
                                        className="fleet-prev"
                                        aria-label="prev"
                                        sx={navBtnSx("left")}
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <IconButton
                                        className="fleet-next"
                                        aria-label="next"
                                        sx={navBtnSx("right")}
                                    >
                                        <ArrowForwardIcon />
                                    </IconButton>
                                </>
                            )}

                            <Swiper
                                modules={[Navigation, Pagination]}
                                navigation={
                                    filteredCars.length >= 3
                                        ? { prevEl: ".fleet-prev", nextEl: ".fleet-next" }
                                        : false
                                }
                                pagination={{
                                    el: ".fleet-dots",
                                    clickable: true,
                                    renderBullet: (index, className) =>
                                        `<span class="${className}"></span>`,
                                }}
                                slidesPerView={"auto"}
                                spaceBetween={0}
                                centeredSlides={false}
                                freeMode={false}
                                slidesOffsetBefore={12}
                                slidesOffsetAfter={12}
                                breakpoints={{
                                    480: { spaceBetween: 0, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
                                    768: { spaceBetween: 0, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
                                    1200: { spaceBetween: 0, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
                                }}
                                // style={{ paddingBottom: 52, paddingTop: 4 }}
                                style={{ paddingBottom: 25, paddingTop: 4 }}
                                className="fleets"
                            >
                                {loader
                                    ? Array.from({ length: 6 }).map((_, i) => (
                                        <SwiperSlide key={`skeleton-${i}`} style={{ width: "min(82vw, 300px)" }}>
                                            <FleetCardSkeleton />
                                        </SwiperSlide>
                                    ))
                                    : filteredCars.map((car, i) => (
                                        <SwiperSlide key={car.id ?? i} style={{ width: "min(82vw, 300px)", marginRight: "0px" }}>
                                            <FleetCardResponsive {...car} />
                                        </SwiperSlide>
                                    ))}

                            </Swiper>
                        </>
                    );
                })()}
            </Box>

        </Box>
    );
}

/* ---------- Card (mobile-scaled) ---------- */
function FleetCardResponsive({ name, image, newImage, year, transmission, fuelType, capacity, model_id }) {
    const router = useRouter()
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid #eceff2",
                boxShadow: "0 10px 28px rgba(0,0,0,.12)",
                bgcolor: "#fff",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                    transform: "scale(1.03)",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                },
            }}
        >
            {/* image block with rounded inner corners */}
            <Box sx={{ position: "relative", height: 160, p: 1.25, pb: 0, m: 2 }}>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 10,
                        borderRadius: 3,
                        overflow: "hidden",
                    }}
                >
                    <img src={`${process.env.IMAGE_BASE_URL}/${newImage}`} alt={name}
                        style={{ objectFit: "contain", position: "absolute", bottom: "0px" }}
                    />
                </Box>
                <IconButton
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: { xs: 34, md: 40 },
                        height: { xs: 34, md: 40 },
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        border: "1px solid #e6eaee",
                        boxShadow: "0 2px 10px rgba(0,0,0,.12)",
                    }}
                    // onClick={() => router.push(`/carBooking/carBookRide?modelIdget=${model_id}`)}
                    onClick={() => {
                        window.location.href = `/carBooking/carBookRide?modelIdget=${model_id}`;
                    }}

                >
                    <ArrowOutwardIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                </IconButton>
            </Box>

            {/* title */}
            <Typography
                sx={{
                    px: { xs: 2, md: 3 },
                    pt: 0,
                    pb: 1.25,
                    fontWeight: 800,
                    textAlign: "start",
                    fontSize: { xs: 16, md: 20 },
                    color: "#111",
                }}
            >
                {name}
            </Typography>

            {/* specs bar */}
            <Paper
                elevation={0}
                sx={{
                    mx: { xs: 1.5, md: 2 },
                    mb: { xs: 1.5, md: 2 },
                    px: { xs: 1, md: 1 },
                    py: { xs: 0.5, md: 0.5 },
                    borderRadius: 3,
                    bgcolor: "#f6f6f6",
                    display: "flex",
                    justifyContent: { xs: "space-around", md: "space-between" },
                    alignItems: "center",
                    gap: { xs: 1, md: 0 },
                }}
            >
                {/* <Spec icon={<CalendarMonthOutlinedIcon sx={specIconSx} />} label={year} /> */}
                <Spec icon="/img/ourfleets/speedometer-01.svg" label={year} />
                <Spec icon="/img/ourfleets/manual-gearbox.svg" label={transmission} />
                <Spec icon="/img/ourfleets/users-01.svg" label={capacity} />
                <Spec icon="/img/ourfleets/gas-station.svg" label={fuelType} />
            </Paper>
        </Paper>
    );
}

function Spec({ icon, label }) {
    const isImage = typeof icon === "string";

    return (
        <Stack
            alignItems="center"
            spacing={0.75}
            sx={{ minWidth: { xs: 52, md: 56 } }}
        >
            <Box
                sx={{
                    width: { xs: 28, md: 28 },
                    height: { xs: 28, md: 28 },
                    display: "grid",
                    placeItems: "center",
                }}
            >
                {isImage ? (
                    <Image
                        src={icon}
                        alt={label}
                        width={20}
                        height={20}
                        style={{ objectFit: "contain" }}
                    />
                ) : (
                    icon
                )}
            </Box>
            <Typography
                sx={{
                    fontSize: { xs: 12, md: 12 },
                    color: "#333",
                    fontWeight: 600,
                }}
            >
                {label}
            </Typography>
        </Stack>
    );
}


const specIconSx = { fontSize: { xs: 16, md: 18 }, color: "#7b8794" };

/* Prev/Next buttons: hidden on mobile, tuned on desktop */
const navBtnSx = (side) => ({
    position: "absolute",
    zIndex: 2,
    top: { xs: "38%", md: "42%" },
    [side]: { xs: 4, md: -12 },
    transform: side === "left" ? "translateX(-10%)" : "translateX(10%)",
    width: { xs: 36, md: 52 },
    height: { xs: 36, md: 52 },
    display: { xs: "none", md: "flex" }, // hide on mobile
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    background: "linear-gradient(180deg, #75c4c5 0%, #0d8196 100%)",
    color: "#fff",
    boxShadow: "0 8px 24px rgba(0,0,0,.25)",
    "&:hover": { background: "linear-gradient(180deg, #4bb6b4 0%, #249f9c 100%)" },
});
